import { CubePresenter, FlipIntent, Logger, Presenter } from 'wdc-cube'

import { MainPresenter } from 'src/main/Main.presenter'

import { WaitingScope } from '../../the-connection/tc_scopes'
import { UserManagerFormKeys } from './form'
import UserManagerKeys from './um_keys'
import { HeaderScope, UserManagerScope } from './um_scopes'
import { UserManagerService } from './um_service'

const LOG = Logger.get('UserManagerPresenter')
const userManagerService = UserManagerService.singleton()

export class UserManagerPresenter extends CubePresenter<MainPresenter, UserManagerScope> {
    // :: Fields

    private readonly __headerBar: Presenter<HeaderScope, UserManagerPresenter>

    // :: Constructor

    public constructor(app: MainPresenter) {
        super(app, new UserManagerScope())
        this.__headerBar = new Presenter(this, this.scope.header)
    }

    // :: Methods

    public override release() {
        this.app.releaseToolbar(this.__headerBar)
        super.release()
        LOG.debug('Finalized')
    }

    public override async applyParameters(
        intent: FlipIntent,
        initialization: boolean,
        last: boolean
    ): Promise<boolean> {
        const keys = new UserManagerKeys(this.app, intent)

        this.app.scope.toolbar = this.__headerBar.scope

        keys.parentSlot(this.scope)

        if (initialization) {
            await this.#initialize()
        }

        keys.parentSlot(this.scope)
        if (last) {
            keys.dialogSlot(undefined)
        }

        if (this.scope.userFilter !== keys.userFilter) {
            this.scope.userFilter = keys.userFilter || ''
        }

        if (!this.scope.users || this.scope.users.length === 0) {
            await this.handleFind(this.scope.userFilter)
        }

        return keys.allow
    }

    public override publishParameters(intent: FlipIntent): void {
        const keys = new UserManagerKeys(this.app, intent)
        keys.userFilter = this.scope.userFilter || undefined
    }

    async #initialize() {
        LOG.debug('initialize')
        this.scope.onFind = this.action(this.handleFind)
        this.scope.onEdit = this.action(this.handleEdit)
        this.scope.onNew = this.action(this.handleNew)

        this.scope.onShowDeletedChange = this.action(this.handleShowDeletedChange)
        this.scope.onDelete = this.action(this.handleDeleteRestore)
        this.scope.onDeleteConfirmTextChanged = this.action(this.handleDeleteConfirmTextChanged)
        this.scope.onConfirmDelete = this.action(this.handleConfirmDeleteRestore)
        this.scope.onCancelConfirmDelete = this.action(this.handleCancelConfirmDelete)
    }

    private async handleFind(nameFilter?: string) {
        try {
            this.scope.waiting = new WaitingScope()
            this.scope.userFilter = nameFilter || ''
            const userRows = await userManagerService.fetchUsers(this.scope.userFilter, this.scope.showDeleted)
            this.scope.users = userRows
        } catch (caught) {
            LOG.error('handleFind', caught)
            throw caught
        } finally {
            this.scope.waiting = undefined
        }
    }

    private async handleEdit(userId: number) {
        const keys = new UserManagerFormKeys(this.app)
        keys.userId = userId
        await keys.flip()
    }

    private async handleNew() {
        await new UserManagerFormKeys(this.app).flip()
    }

    private async handleShowDeletedChange() {
        this.scope.showDeleted = !this.scope.showDeleted
        await this.handleFind(this.scope.userFilter)
    }

    private async handleDeleteRestore(userId: number) {
        const user = this.scope.users.find((g) => Number(g.id) === userId)
        this.scope.userToDelete = user
        this.scope.deleteConfirmText = ''
        this.scope.deleteModalOpen = true
    }

    private async handleDeleteConfirmTextChanged(text: string) {
        this.scope.deleteConfirmText = text
    }

    private async handleCancelConfirmDelete() {
        this.scope.deleteModalOpen = false
        this.scope.userToDelete = undefined
        this.scope.deleteConfirmText = ''
    }

    private async handleConfirmDeleteRestore() {
        if (this.scope.userToDelete && this.scope.userToDelete.id) {
            try {
                this.scope.waiting = new WaitingScope()
                if (this.scope.userToDelete.deleted_date) {
                    await userManagerService.restoreUser(this.scope.userToDelete.id)
                    this.app.notify('success', 'Usuário restaurado com sucesso')
                } else {
                    await userManagerService.deleteUser(this.scope.userToDelete.id)
                    this.app.notify('success', 'Usuário excluído com sucesso')
                }
                this.scope.deleteModalOpen = false
                this.scope.userToDelete = undefined
                this.scope.deleteConfirmText = ''
                await this.handleFind(this.scope.userFilter)
            } catch (caught) {
                LOG.error('handleConfirmDelete', caught)
                throw caught
            } finally {
                this.scope.waiting = undefined
            }
        }
    }
}

export default UserManagerPresenter
